/* eslint-disable */

import React, { useCallback, useState } from 'react'
import { View, ActivityIndicator, ViewStyle, FlatList, RefreshControl } from 'react-native'
import { observer } from 'mobx-react-lite'
import { FeedProps } from './feed.props'
import { useStores } from '@trivie/core'
import { Post } from '../post/post'
import { color } from '../../theme/color'

const CONTAINER: ViewStyle = {
  flex: 1,
  alignItems: 'stretch',
  justifyContent: 'space-between',
  backgroundColor: color.border,
}

export const Feed: React.FunctionComponent<FeedProps> = observer((props) => {
  const { talkStore, resourceStore } = useStores()
  const {
    sortedPosts,
    sortedTopicPosts,
    setSelectedPost,
    likePost,
    currentPage,
    currentTopicPage,
    getPosts,
    getPostsForTopic,
    refreshPosts,
    fetchingPosts,
    deletePost,
    refreshTopicPosts,
    posts,
  } = talkStore
  const { navigation, route, style, selectedTopicId } = props

  const goToComments = React.useMemo(
    () => (id: number) => props.navigation.push('post', { postID: id }),
    [navigation],
  )

  const onComment = (post: any) => {
    setSelectedPost(post)
    if (post.resources) {
      talkStore.setSelectedPostPhoto(post.resources)
    }
    goToComments(post.id ?? post.post_id)
  }

  const onLike = (postId: number) => {
    likePost(postId).then(() => {
      refreshTopicPosts(selectedTopicId ?? route?.params?.selectedTopicId)
    })
  }
  const onDelete = (postId: number) => {
    deletePost(postId).then(() => {
      refreshPosts()
    })
  }

  const onRefresh = () => {
    if (selectedTopicId || route?.params?.selectedTopicId) {
      refreshTopicPosts(selectedTopicId ?? route?.params?.selectedTopicId)
    } else {
      refreshPosts()
    }
  }

  const onEndReached = () => {
    if (
      (selectedTopicId || route?.params?.selectedTopicId) &&
      talkStore.lastPage &&
      talkStore.currentPage < talkStore.lastPage
    ) {
      getPostsForTopic(selectedTopicId ?? route?.params?.selectedTopicId, currentTopicPage + 1)
    } else if (talkStore.lastPage && talkStore.currentPage < talkStore.lastPage) {
      getPosts(currentPage + 1)
    }
  }

  const [scrollEnd, setScrollEnd] = useState(false)

  const renderItem = ({ item, index }: { item: any; index: number }) => {
    let photos = [] as any
    let video
    if (item.resources && item.resources.length > 0) {
      item.resources.forEach((resource: any) => {
        if (
          ['jpg', 'webp', 'jpeg', 'png', 'gif'].includes(
            (resource.extension ?? '').toLocaleLowerCase(),
          )
        ) {
          photos.push(resource)
        } else {
          video = resource
        }
      })
    }

    return photos && photos.length > 0 ? (
      <Post
        navigation={navigation}
        type={'photo'}
        title={`${item.author.first_name} ${item.author.last_name}`}
        subtitle={item.title}
        avatar={item.author.photo_url}
        post={item}
        onComment={() => onComment(item)}
        onLike={() => onLike(item.id)}
        onDelete={() => onDelete(item.id)}
        onTogglePin={refreshPosts}
        index={index}
        photos={photos}
        recent={selectedTopicId}
        style={{ paddingHorizontal: selectedTopicId ? 20 : 0 }}
      />
    ) : video ? (
      <Post
        navigation={navigation}
        type={'video'}
        style={{ paddingHorizontal: selectedTopicId ? 20 : 0 }}
        title={`${item.author.first_name} ${item.author.last_name}`}
        subtitle={item.title}
        avatar={item.author.photo_url}
        post={item}
        onComment={() => onComment(item)}
        onLike={() => onLike(item.id)}
        onDelete={() => onDelete(item.id)}
        onTogglePin={refreshPosts}
        index={index}
        video={video}
        currentViewableIndex={currentViewableIndex}
        recent={selectedTopicId}
      />
    ) : (
      <Post
        navigation={navigation}
        type={'text'}
        style={{ paddingHorizontal: selectedTopicId ? 20 : 0 }}
        title={`${item.author.first_name} ${item.author.last_name}`}
        subtitle={item.title}
        avatar={item.author.photo_url}
        post={item}
        onComment={() => onComment(item)}
        onLike={() => onLike(item.id)}
        onDelete={() => onDelete(item.id)}
        onTogglePin={refreshPosts}
        index={index}
        recent={selectedTopicId}
      />
    )
  }

  const [currentViewableIndex, setCurrentViewableIndex]: any = useState(0)
  const [viewedItems, setViewedItems]: any = useState([])
  const handleVieweableItemsChanged = useCallback(({ changed }) => {
    setViewedItems((oldViewedItems: any) => {
      // We can have access to the current state without adding it
      //  to the useCallback dependencies
      let newViewedItems: any = null
      changed.forEach(({ index, isViewable }: any) => {
        setCurrentViewableIndex(index)
        if (index !== null && isViewable && !oldViewedItems.includes(index)) {
          if (newViewedItems === null) {
            newViewedItems = [...oldViewedItems]
          }
          newViewedItems.push(index)
        }
      })
      // If the items didn't change, we return the old items so
      //  an unnecessary re-render is avoided.
      return newViewedItems === null ? oldViewedItems : newViewedItems
    })
    return []
    // Since it has no dependencies, this function is created only once
  }, [])

  let topicsData = posts as any
  if (selectedTopicId || route?.params?.selectedTopicId) {
    topicsData = sortedTopicPosts
  }

  return (
    <View
      style={{
        ...CONTAINER,
        backgroundColor: selectedTopicId ? color.white : color.border,
        ...style,
      }}
    >
      <FlatList
        showsVerticalScrollIndicator={false}
        data={topicsData.slice()}
        extraData={talkStore.status}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl
            style={{ backgroundColor: selectedTopicId ? color.white : color.border }}
            refreshing={talkStore.isLoading || resourceStore.loadingResourcePost}
            onRefresh={onRefresh}
          />
        }
        keyExtractor={(post) => post.id + ''}
        onEndReachedThreshold={4}
        onViewableItemsChanged={handleVieweableItemsChanged}
        onEndReached={() => setScrollEnd(true)}
        onMomentumScrollEnd={() => {
          scrollEnd && onEndReached()
          setScrollEnd(false)
        }}
        viewabilityConfig={{
          viewAreaCoveragePercentThreshold: 100,
        }}
        ListFooterComponent={
          fetchingPosts ? (
            <ActivityIndicator color="#999" style={{ margin: 10 }} size="small" />
          ) : (
            <View style={{ height: 60 }} />
          )
        }
        ItemSeparatorComponent={() => (
          <View
            style={{ height: 8, backgroundColor: selectedTopicId ? '#F5F5F7' : color.border }}
          />
        )}
      />
    </View>
  )
})
